import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";

import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Typography
} from "@material-ui/core";

import {
  CheckCircleOutline,
  SystemUpdateAlt
} from "@material-ui/icons";

import api from "../../services/api";

const RELEASE_STORAGE_KEY =
  "koraxAcknowledgedReleaseVersion";

const RELEASE_SEEN_PREFIX =
  "koraxReleaseSeen:";

const BASE_RELEASE_VERSION =
  "15.0.2";

const UPDATE_LOCK_KEY =
  "koraxReleaseUpdateLock";

const CHECK_INTERVAL_MS =
  5 * 60 * 1000;

const VersionControl = () => {
  const mountedRef = useRef(true);

  const [release, setRelease] =
    useState(null);

  const [open, setOpen] =
    useState(false);

  const [updating, setUpdating] =
    useState(false);

  const checkCurrentRelease =
    useCallback(async () => {
      try {
        const response = await api.get(
          "/releases/current",
          {
            params: {
              timestamp: Date.now()
            }
          }
        );

        const currentRelease =
          response.data?.release;

        const currentVersion =
          response.data?.version;

        if (
          !mountedRef.current ||
          !currentVersion
        ) {
          return;
        }

        const seenKey =
          `${RELEASE_SEEN_PREFIX}${currentVersion}`;

        const alreadySeen =
          window.localStorage.getItem(
            seenKey
          ) === "true";

        /*
         * A versão-base não deve aparecer como novidade.
         */
        if (
          currentVersion ===
          BASE_RELEASE_VERSION
        ) {
          window.localStorage.setItem(
            seenKey,
            "true"
          );

          window.localStorage.setItem(
            RELEASE_STORAGE_KEY,
            currentVersion
          );

          return;
        }

        if (alreadySeen) {
          return;
        }

        setRelease(
          currentRelease || {
            version: currentVersion,
            title:
              "Nova versão da Korax",
            description:
              "Uma nova atualização está disponível.",
            changelog: [],
            mandatory: false
          }
        );

        setOpen(true);
      } catch (error) {
        console.warn(
          "[Korax Release] Falha ao verificar atualização:",
          error
        );
      }
    }, []);

  useEffect(() => {
    mountedRef.current = true;

    const initialTimer =
      window.setTimeout(
        checkCurrentRelease,
        2500
      );

    const interval =
      window.setInterval(
        checkCurrentRelease,
        CHECK_INTERVAL_MS
      );

    const handleVisibilityChange = () => {
      if (
        document.visibilityState ===
        "visible"
      ) {
        checkCurrentRelease();
      }
    };

    window.addEventListener(
      "focus",
      checkCurrentRelease
    );

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    return () => {
      mountedRef.current = false;

      window.clearTimeout(initialTimer);
      window.clearInterval(interval);

      window.removeEventListener(
        "focus",
        checkCurrentRelease
      );

      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
    };
  }, [checkCurrentRelease]);

  const handleLater = () => {
    if (release?.mandatory) {
      return;
    }

    if (release?.version) {
      window.localStorage.setItem(
        `${RELEASE_SEEN_PREFIX}${release.version}`,
        "true"
      );

      window.localStorage.setItem(
        RELEASE_STORAGE_KEY,
        release.version
      );
    }

    setOpen(false);
  };

  const handleUpdate = async () => {
    if (
      !release?.version ||
      updating
    ) {
      return;
    }

    setUpdating(true);

    try {
      window.localStorage.setItem(
        RELEASE_STORAGE_KEY,
        release.version
      );

      window.localStorage.setItem(
        `${RELEASE_SEEN_PREFIX}${release.version}`,
        "true"
      );

      window.sessionStorage.setItem(
        UPDATE_LOCK_KEY,
        release.version
      );

      if (
        "serviceWorker" in navigator
      ) {
        const registrations =
          await navigator
            .serviceWorker
            .getRegistrations();

        await Promise.all(
          registrations.map(
            registration =>
              registration.update()
          )
        );
      }

      const url =
        new URL(
          window.location.href
        );

      url.searchParams.set(
        "koraxUpdate",
        Date.now()
      );

      window.location.replace(
        url.toString()
      );
    } catch (error) {
      console.warn(
        "[Korax Release] Falha ao atualizar:",
        error
      );

      window.location.reload();
    }
  };

  if (!release) {
    return null;
  }

  const changelog =
    Array.isArray(release.changelog)
      ? release.changelog
      : [];

  return (
    <Dialog
      open={open}
      onClose={handleLater}
      maxWidth="xs"
      fullWidth
      disableBackdropClick={
        Boolean(release.mandatory)
      }
      disableEscapeKeyDown={
        Boolean(release.mandatory)
      }
      PaperProps={{
        style: {
          overflow: "hidden",
          borderRadius: 24,
          background: "#FFFFFF",
          boxShadow:
            "0 28px 80px rgba(15,23,42,.28)"
        }
      }}
    >
      <Box
        style={{
          position: "relative",
          overflow: "hidden",
          padding: "27px 25px 24px",
          color: "#FFFFFF",
          background:
            "linear-gradient(135deg,#071329 0%,#0B2C68 52%,#155EEF 100%)"
        }}
      >
        <Box
          style={{
            position: "absolute",
            width: 190,
            height: 190,
            right: -72,
            top: -105,
            borderRadius: "50%",
            background:
              "rgba(255,255,255,.09)"
          }}
        />

        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          style={{
            position: "relative",
            width: 54,
            height: 54,
            borderRadius: 17,
            color: "#BFDBFE",
            background:
              "rgba(255,255,255,.11)",
            border:
              "1px solid rgba(255,255,255,.14)"
          }}
        >
          <SystemUpdateAlt />
        </Box>

        <Typography
          style={{
            position: "relative",
            marginTop: 17,
            color: "#BFDBFE",
            fontSize: 10,
            fontWeight: 900,
            letterSpacing: ".11em",
            textTransform: "uppercase"
          }}
        >
          Nova versão disponível
        </Typography>

        <Typography
          style={{
            position: "relative",
            marginTop: 7,
            color: "#FFFFFF",
            fontSize: 22,
            lineHeight: 1.15,
            fontWeight: 900,
            letterSpacing: "-.035em"
          }}
        >
          {release.title ||
            "Korax foi atualizada"}
        </Typography>

        <Typography
          style={{
            position: "relative",
            marginTop: 9,
            color:
              "rgba(219,234,254,.76)",
            fontSize: 12,
            lineHeight: 1.6
          }}
        >
          {release.description ||
            "Atualize para utilizar a versão mais recente da plataforma."}
        </Typography>

        <Box
          display="inline-flex"
          style={{
            position: "relative",
            marginTop: 14,
            padding: "5px 9px",
            borderRadius: 999,
            color: "#DBEAFE",
            background:
              "rgba(255,255,255,.10)",
            fontSize: 10,
            fontWeight: 850
          }}
        >
          Versão {release.version}
        </Box>
      </Box>

      <DialogContent
        style={{
          padding: "22px 24px 24px"
        }}
      >
        {changelog.length > 0 && (
          <>
            <Typography
              style={{
                color: "#0F172A",
                fontSize: 12,
                fontWeight: 900
              }}
            >
              O que há de novo
            </Typography>

            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
                marginTop: 13
              }}
            >
              {changelog.map(
                (item, index) => (
                  <Box
                    key={`${release.version}-${index}`}
                    display="flex"
                    alignItems="flex-start"
                    style={{ gap: 9 }}
                  >
                    <CheckCircleOutline
                      style={{
                        marginTop: 1,
                        color: "#22C55E",
                        fontSize: 17
                      }}
                    />

                    <Typography
                      style={{
                        color: "#475569",
                        fontSize: 12,
                        lineHeight: 1.5
                      }}
                    >
                      {item}
                    </Typography>
                  </Box>
                )
              )}
            </Box>
          </>
        )}

        {release.mandatory && (
          <Box
            style={{
              marginTop: 17,
              padding: 12,
              borderRadius: 12,
              color: "#991B1B",
              background:
                "rgba(254,226,226,.72)",
              border:
                "1px solid rgba(239,68,68,.15)",
              fontSize: 11,
              fontWeight: 750,
              lineHeight: 1.5
            }}
          >
            Esta atualização é obrigatória para continuar
            utilizando a Korax.
          </Box>
        )}

        <Box
          display="flex"
          style={{
            gap: 10,
            marginTop: 21
          }}
        >
          {!release.mandatory && (
            <Button
              type="button"
              fullWidth
              onClick={handleLater}
              disabled={updating}
              style={{
                minHeight: 42,
                borderRadius: 11,
                color: "#64748B",
                background: "#F1F5F9",
                fontWeight: 800,
                textTransform: "none"
              }}
            >
              Depois
            </Button>
          )}

          <Button
            type="button"
            fullWidth
            onClick={handleUpdate}
            disabled={updating}
            style={{
              minHeight: 42,
              borderRadius: 11,
              color: "#FFFFFF",
              background:
                "linear-gradient(135deg,#2563EB,#4F46E5)",
              boxShadow:
                "0 10px 22px rgba(37,99,235,.22)",
              fontWeight: 850,
              textTransform: "none"
            }}
          >
            {updating
              ? "Atualizando..."
              : "Atualizar agora"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default VersionControl;
